import React, {useEffect, useState} from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import MissionSidebar from '../../components/mission/MissionSidebar';
import {api} from '../../lib/api';
import {quests} from '../../data/mock';
import styles from './QuestMap.module.scss';

function QuestNode ({quest, index}) {
    const {state, title, moduleName, icon = 'star', progress} = quest;
    const locked = state === 'locked';
    const done = state === 'done';
    const current = state === 'current';
    const nodeClassName = [styles.node, styles[state]].join(' ');
    const nodeLabel = locked ? `${title}（完成前面的任务后解锁）` : `${title}，进入课程`;

    return (
        <li className={[styles.nodeRow, styles[`row_${state}`]].join(' ')}>
            <div className={styles.stepRail} aria-hidden="true">
                <span className={styles.stepNumber}>{index + 1}</span>
                {locked ? (
                    <span className={nodeClassName}>
                        <Icon name="lock" size={22} />
                    </span>
                ) : (
                    <span className={nodeClassName}>
                        <Icon name={done ? 'check' : icon} size={22} />
                    </span>
                )}
            </div>

            <div className={styles.lessonCard}>
                <div className={styles.lessonMeta}>
                    <span className={styles.moduleName}>{moduleName || 'Scratch 基础'}</span>
                    {current && <span className={styles.currentTag}>进行中</span>}
                    {done && <span className={styles.doneTag}>已完成</span>}
                    {locked && <span className={styles.lockedTag}>未解锁</span>}
                </div>
                <h2 className={styles.lessonTitle}>{title}</h2>
                {(current || done) && (
                    <span
                        className={styles.miniTrack}
                        role="progressbar"
                        aria-label={`${title}进度`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progress}
                    >
                        <span className={styles.miniFill} style={{width: `${progress}%`}} />
                    </span>
                )}
                <div className={styles.lessonActions}>
                    {locked ? (
                        <span className={styles.lockedCopy}>完成前面的任务后解锁</span>
                    ) : (
                        <Button to={`/lab/${quest.id}`} variant={current ? 'secondary' : 'soft'} size="sm" aria-label={nodeLabel}>
                            {current ? '继续学习' : '进入课程'}
                        </Button>
                    )}
                </div>
            </div>
        </li>
    );
}

export default function QuestMap () {
    const [questList, setQuestList] = useState(quests);
    const [source, setSource] = useState('loading');

    useEffect(() => {
        let alive = true;
        api.listQuests().then(({quests: remoteQuests}) => {
            if (!alive) return;
            setQuestList(remoteQuests.map((q, index) => ({...q, icon: quests[index]?.icon ?? 'star'})));
            setSource('backend');
        }).catch(() => setSource('offline'));
        return () => {
            alive = false;
        };
    }, []);

    const ordered = questList;
    const currentQuest = questList.find(q => q.state === 'current') ?? questList[0];
    const doneCount = questList.filter(q => q.state === 'done').length;
    const unlockedCount = questList.filter(q => q.state !== 'locked').length;

    return (
        <div className={`cq-container ${styles.layout}`}>
            <aside className={styles.aside}>
                <MissionSidebar />
            </aside>

            <section className={styles.mapWrap} aria-labelledby="quest-map-title">
                <div className={styles.mapHeader}>
                    <div>
                        <span className={styles.kicker}>编程路线图</span>
                        <h1 id="quest-map-title" className={styles.title}>选择下一站</h1>
                        <p className={styles.subtitle}>从基础事件到逻辑挑战，一关一关点亮你的 Scratch 技能。</p>
                    </div>
                    <div className={styles.routeStats} aria-label="路线进度">
                        <span><strong>{doneCount}</strong> 已完成</span>
                        <span><strong>{unlockedCount}</strong> 已解锁</span>
                        <span><strong>{questList.length}</strong> 总关卡</span>
                    </div>
                </div>

                <div className={styles.mapToolbar}>
                    <div className={styles.sourceBadge} aria-live="polite">{source === 'backend' ? '后端任务进度' : source === 'loading' ? '连接任务 API…' : '离线任务数据'}</div>
                    <div className={styles.mapActions}>
                        <div className={styles.nextQuest}>
                            <Icon name="play" size={17} />
                            <span>下一站</span>
                            <strong>{currentQuest.title}</strong>
                        </div>
                        <Button
                            to={`/lab/${currentQuest.id}`}
                            variant="secondary"
                            size="sm"
                            iconRight={<Icon name="arrowRight" size={18} />}
                        >
                            继续冒险
                        </Button>
                    </div>
                </div>

                <div className={styles.map} style={{'--quest-count': ordered.length}}>
                    <ol className={styles.nodes}>
                        {ordered.map((q, i) => (
                            <QuestNode key={q.id} quest={q} index={i} />
                        ))}
                    </ol>
                </div>
            </section>
        </div>
    );
}
